import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import './Outreach.css';
import {
    getCompanyId,
    subscribeToCampaigns,
    createCampaign,
    getShortlists
} from '../../services/outreachService';

const OutreachCampaigns = ({ onSelectCampaign }) => {
  useUiLanguage();
    const { t } = useTranslation('sourcing');
    const [activeTab, setActiveTab] = useState('all');

    // Firebase state
    const [companyId, setCompanyId] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [shortlists, setShortlists] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newCampaignName, setNewCampaignName] = useState('');
    const [selectedShortlist, setSelectedShortlist] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const translateDate = (dateStr) => {
        if (!dateStr) return '';
        // Handle timestamp
        if (typeof dateStr === 'number') {
            const date = new Date(dateStr);
            const month = date.toLocaleString('default', { month: 'long' });
            const year = date.getFullYear();
            return `${t(`outreach.months.${month.toLowerCase()}`, month)} ${year}`;
        }
        const parts = dateStr.split(' ');
        if (parts.length !== 2) return dateStr;
        const [month, year] = parts;
        const monthKey = month.toLowerCase();
        return `${t(`outreach.months.${monthKey}`, month)} ${year}`;
    };

    const getCompletionPercentage = (stats) => {
        if (!stats) return 0;
        if (stats.completionPercentage !== undefined) return stats.completionPercentage;
        const total = stats.totalCandidates || stats.total || 0;
        const completed = stats.completed || 0;
        return total > 0 ? Math.round((completed / total) * 100) : 0;
    };

    // Initialize auth and company ID
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const cid = await getCompanyId();
                    setCompanyId(cid);
                } catch (err) {
                    console.error('Error getting company ID:', err);
                    setError(uiText('Failed to load company data'));
                    setIsLoading(false);
                }
            } else {
                setCompanyId(null);
                setCampaigns([]);
                setIsLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    // Subscribe to campaigns when companyId is available
    useEffect(() => {
        if (!companyId) return;

        setIsLoading(true);
        const unsubscribe = subscribeToCampaigns(companyId, (campaignsData) => {
            setCampaigns(campaignsData);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [companyId]);

    // Fetch shortlists when modal opens
    useEffect(() => {
        if (!showCreateModal || !companyId) return;

        const fetchShortlists = async () => {
            try {
                const lists = await getShortlists(companyId);
                setShortlists(lists);
            } catch (err) {
                console.error('Error fetching shortlists:', err);
            }
        };

        fetchShortlists();
    }, [showCreateModal, companyId]);

    const handleCreateNewClick = () => {
        setNewCampaignName('');
        setSelectedShortlist('');
        setShowCreateModal(true);
    };

    const handleConfirmCreate = async () => {
        if (!newCampaignName || !selectedShortlist || !companyId) return;

        setIsCreating(true);
        try {
            const newCampaign = await createCampaign(companyId, {
                name: newCampaignName,
                shortlistId: selectedShortlist,
                date: new Date().toLocaleString('default', { month: 'long', year: 'numeric' })
            });

            setShowCreateModal(false);

            // Navigate to the new campaign
            onSelectCampaign({
                ...newCampaign,
                id: newCampaign.id,
                name: newCampaignName,
                status: 'draft',
                steps: 0,
                shortlistName: selectedShortlist
            });
        } catch (err) {
            console.error('Error creating campaign:', err);
            setError(uiText('Failed to create campaign'));
        } finally {
            setIsCreating(false);
        }
    };

    const getStatusDisplay = (status) => {
        switch (status) {
            case 'draft':
                return t('outreach.tabs.drafts');
            case 'active':
                return t('outreach.tabs.active');
            case 'paused':
                return t('outreach.status.paused', 'Paused');
            case 'completed':
                return t('outreach.tabs.completed');
            default:
                return status;
        }
    };

    // Filter campaigns based on active tab
    const filteredCampaigns = campaigns.filter(c => {
        const percentage = getCompletionPercentage(c.stats);
        const effectiveStatus = (percentage === 100 && c.status !== 'draft') ? 'completed' : c.status;

        if (activeTab === 'all') return true;
        if (activeTab === 'drafts') return effectiveStatus === 'draft';
        if (activeTab === 'active') return effectiveStatus === 'active' || effectiveStatus === 'paused';
        return effectiveStatus === activeTab;
    });

    if (isLoading) {
        return (
            <div className="outreach-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <div style={{ textAlign: 'center' }}>
                    <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '32px', color: '#568ded', marginBottom: '16px' }}></i>
                    <p style={{ color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)' }}>{t('common.loading', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="outreach-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                <div style={{ textAlign: 'center', color: 'var(--app-tone-e74c3c, #e74c3c)' }}>
                    <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '32px', marginBottom: '16px' }}></i>
                    <p>{uiText(error)}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="outreach-content">
            <div className="outreach-header-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="outreach-subtitle" style={{ fontSize: '24px', fontWeight: "var(--app-weight-strong, 800)" }}>
                    {t('outreach.myCampaigns', 'My Outreach Campaigns')}
                </h2>
                <button className="btn-create-campaign" onClick={handleCreateNewClick}>
                    <i className="fa-solid fa-plus"></i>
                    {t('outreach.createNew', 'Create New')}
                </button>
            </div>

            <div className="outreach-tabs">
                <button
                    className={`outreach-tab ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    {t('outreach.tabs.all', 'All Campaigns')}
                </button>
                <button
                    className={`outreach-tab ${activeTab === 'drafts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('drafts')}
                >
                    {t('outreach.tabs.drafts', 'Drafts')}
                </button>
                <button
                    className={`outreach-tab ${activeTab === 'active' ? 'active' : ''}`}
                    onClick={() => setActiveTab('active')}
                >
                    {t('outreach.tabs.active', 'Active')}
                </button>
                <button
                    className={`outreach-tab ${activeTab === 'completed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('completed')}
                >
                    {t('outreach.tabs.completed', 'Completed')}
                </button>
            </div>

            <div className="campaigns-list">
                {filteredCampaigns.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)' }}>
                        <i className="fa-regular fa-envelope" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}></i>
                        <p style={{ fontSize: '16px' }}>{t('outreach.noCampaigns', 'No campaigns yet. Create your first one!')}</p>
                    </div>
                ) : (
                    filteredCampaigns.map(campaign => {
                        const percentage = getCompletionPercentage(campaign.stats);
                        // Force completed status if 100%, regardless of database status (unless draft)
                        const effectiveStatus = (percentage === 100 && campaign.status !== 'draft') ? 'completed' : campaign.status;

                        return (
                            <div
                                key={campaign.id}
                                className="campaign-card"
                                onClick={() => onSelectCampaign({
                                    ...campaign,
                                    shortlistName: campaign.shortlistId
                                })}
                            >
                                <div className="campaign-header">
                                    <div>
                                        <div className="campaign-title-row">
                                            <span className="campaign-title">{campaign.name}</span>
                                            <i className="fa-regular fa-envelope" style={{ color: '#568ded' }}></i>
                                        </div>
                                        <div className="campaign-meta">
                                            {t('outreach.stats.steps', { count: campaign.nodes?.length || 0 })} &nbsp; <span style={{ opacity: 0.5 }}>|</span> &nbsp; {translateDate(campaign.createdAt || campaign.date)}
                                        </div>
                                    </div>

                                    <div className="campaign-actions">
                                        <span className="tag-pill">{campaign.shortlistId || campaign.shortlistName}</span>
                                        <span className={`status-badge ${effectiveStatus}`}>
                                            <span className="dot"></span>
                                            {getStatusDisplay(effectiveStatus)}
                                        </span>
                                        <div className="btn-arrow">
                                            <i className="fa-solid fa-chevron-right"></i>
                                        </div>
                                    </div>
                                </div>

                                {(['active', 'paused', 'completed'].includes(effectiveStatus)) && campaign.stats && (
                                    <div className="campaign-stats-row">
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <div style={{
                                                width: '54px', height: '54px', borderRadius: '50%',
                                                background: `conic-gradient(#2ecc71 0% ${percentage}%, rgba(255,255,255,0.1) ${percentage}% 100%)`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                position: 'relative'
                                            }}>
                                                <div style={{
                                                    width: '44px', height: '44px', borderRadius: '50%',
                                                    background: 'var(--app-surface, #0a1930)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '13px', fontWeight: "var(--app-weight-strong, 800)", color: 'var(--app-tone-2ecc71, #2ecc71)'
                                                }}>
                                                    {percentage}%
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '10px', color: 'var(--app-tone-2ecc71, #2ecc71)', marginTop: '6px', fontWeight: "var(--app-weight-strong, 700)", textTransform: 'none', letterSpacing: '0.5px' }}>{t('outreach.stats.complete')}</div>
                                        </div>

                                        <div className="stat-item">
                                            <span className="stat-val">{campaign.stats.totalCandidates || campaign.stats.total || 0}</span>
                                            <span className="stat-label">{t('outreach.stats.totalCandidates')}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-val">{campaign.stats.completed || 0}</span>
                                            <span className="stat-label">{t('outreach.stats.completedSequences')}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-val">{campaign.stats.active || 0}</span>
                                            <span className="stat-label">{t('outreach.stats.activeSequences')}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-val">{campaign.stats.replied || 0}</span>
                                            <span className="stat-label">{t('outreach.stats.replied')}</span>
                                        </div>

                                        {/* Visual Sequence Preview */}
                                        <div className="sequence-preview">
                                            {campaign.nodes?.map((node, idx) => (
                                                <React.Fragment key={node.id || idx}>
                                                    <div className="seq-node-preview">
                                                        <i className={node.type === 'email' ? 'fa-solid fa-envelope' : 'fa-regular fa-clock'}></i>
                                                    </div>
                                                    {idx < (campaign.nodes?.length || 0) - 1 && <div className="seq-line-preview"></div>}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Create Campaign Modal */}
            {showCreateModal && (
                <div className="modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div className="modal-content" style={{
                        background: '#fff', width: '500px', padding: '30px', borderRadius: '12px', color: "var(--app-neutral-ink, #000)"
                    }}>
                        <h3 style={{ fontFamily: 'Montserrat', fontWeight: "var(--app-weight-strong, 700)", marginBottom: '24px', textAlign: 'center' }}>
                            {t('outreach.modal.title')}
                        </h3>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>{t('outreach.modal.campaignName')}</label>
                            <input
                                type="text"
                                placeholder={t('outreach.modal.campaignNamePlaceholder')}
                                value={newCampaignName}
                                onChange={(e) => setNewCampaignName(e.target.value)}
                                style={{
                                    width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd',
                                    fontFamily: 'Montserrat', fontSize: '14px'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '30px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>{t('outreach.modal.selectShortlist')}</label>
                            <select
                                value={selectedShortlist}
                                onChange={(e) => setSelectedShortlist(e.target.value)}
                                style={{
                                    width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd',
                                    fontFamily: 'Montserrat', fontSize: '14px', background: 'white'
                                }}
                            >
                                <option value="">{t('outreach.modal.selectShortlistPlaceholder')}</option>
                                {shortlists.map(sl => (
                                    <option key={sl.name} value={sl.name}>
                                        {sl.name} ({sl.profilesCount} {t('outreach.candidates.label', 'candidates')})
                                    </option>
                                ))}
                            </select>
                            {shortlists.length === 0 && (
                                <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                                    {t('outreach.modal.noShortlists', 'No shortlists found. Create one in Sourcing first.')}
                                </p>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                disabled={isCreating}
                                style={{
                                    padding: '10px 24px', borderRadius: '6px', background: '#eee', border: 'none',
                                    fontFamily: 'Montserrat', cursor: 'pointer', fontWeight: 600, color: "var(--app-neutral-ink, #333)"
                                }}
                              data-light-control={"quiet"}>
                                {t('outreach.modal.cancel')}
                            </button>
                            <button
                                onClick={handleConfirmCreate}
                                disabled={!newCampaignName || !selectedShortlist || isCreating}
                                style={{ '--light-control-fill': (!newCampaignName || !selectedShortlist || isCreating) ? '#ccc' : '#568ded',
                                    padding: '10px 24px', borderRadius: '6px',
                                    background: (!newCampaignName || !selectedShortlist || isCreating) ? '#ccc' : '#568ded',
                                    color: !newCampaignName || !selectedShortlist || isCreating ? 'var(--app-text, #fff)' : "#FFFFFF", border: 'none', fontFamily: 'Montserrat', fontWeight: 600,
                                    cursor: (!newCampaignName || !selectedShortlist || isCreating) ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '8px'
                                }}
                              data-light-control={!newCampaignName || !selectedShortlist || isCreating ? "quiet" : "filled"}>
                                {isCreating && <i className="fa-solid fa-spinner fa-spin"></i>}
                                {t('outreach.modal.createButton')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OutreachCampaigns;
